import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
// Elements movido para cada página individualmente (Checkout, Payment, PaymentSuccess)
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { UnreadCountProvider } from "./contexts/UnreadCountContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import Header from "./components/Header";
import LanguageSync from "./components/LanguageSync";
import GoogleAdsTracker from "./components/GoogleAdsTracker";
import LandingPage from "./pages/LandingPage";
import UscisCampaignLanding from "./pages/UscisCampaignLanding";
import { LANDING_PAGES } from "./lib/landingContent";
import UniversalLayout from "./components/UniversalLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import EmployeeTabRoute from "./components/EmployeeTabRoute";
import { getFirestore, collection } from "firebase/firestore";
import { safeOnSnapshot } from "./lib/safeSnapshot";
import "./global.css";

// Importações das páginas
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import TermsOfUse from "./pages/TermsOfUse";

// Client (lazy — carregado sob demanda; fora do bundle inicial público)
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const ClientProfile = lazy(() => import("./pages/client/ClientProfile"));
const ClientProjects = lazy(() => import("./pages/client/ClientProjects"));
const ClientAddProject = lazy(() => import("./pages/client/ClientAddProject"));
const ClienteProjectDetails = lazy(() => import("./pages/client/ClienteProjectDetails"));
const CheckoutPage = lazy(() => import("./pages/client/CheckoutPage"));
const PaymentSuccess = lazy(() => import("./pages/client/PaymentSuccess"));
const PaymentError = lazy(() => import("./pages/client/PaymentError"));
const AddCollaboratorColab = lazy(() => import("./pages/client/AddCollaboratorColab"));
const ApplePayTest = lazy(() => import("./pages/client/ApplePayTest"));
const ClientBudget = lazy(() => import("./pages/client/ClientBudget"));
const ClientDrafts = lazy(() => import("./pages/client/ClientDrafts"));
const ClientProjectsDone = lazy(() => import("./pages/client/ClientProjectsDone"));
const ClientProjectsPaid = lazy(() => import("./pages/client/ClientProjectsPaid"));
const ClientPayments = lazy(() => import("./pages/client/ClientPayments"));
const ClientGoingOn = lazy(() => import("./pages/client/ClientGoingOn"));
const ClientAnalysis = lazy(() => import("./pages/client/ClientAnalysis"));
const ClientBudgetReady = lazy(() => import("./pages/client/ClientBudgetReady"));
const ClientRefund = lazy(() => import("./pages/client/ClientRefund"));
const ClientDivergence = lazy(() => import("./pages/client/ClientDivergence"));
const ClientIntegrations = lazy(() => import("./pages/client/ClientIntegrations"));

// Master / Employee (lazy — sob demanda)
const MasterClient = lazy(() => import("./pages/company/master/MasterClient"));
const MasterEmployee = lazy(() => import("./pages/company/master/MasterEmployee"));
const MasterEmployeeRegister = lazy(() => import("./pages/company/master/MasterEmployeeRegister"));
const MasterEmployeeTypeRegister = lazy(() => import("./pages/company/master/MasterEmployeeTypeRegister"));
const MasterEmployeeManage = lazy(() => import("./pages/company/master/MasterEmployeeManage"));
const MasterEmailTemplates = lazy(() => import("./pages/company/master/MasterEmailTemplates"));
const MasterFilesAll = lazy(() => import("./pages/company/master/MasterFilesAll"));
const MasterProductivity = lazy(() => import("./pages/company/master/MasterProductivity"));
const MasterFileObservations = lazy(() => import("./pages/company/master/MasterFileObservations"));
const MasterEmployeePayments = lazy(() => import("./pages/company/master/MasterEmployeePayments"));
const MasterFilesOngoing = lazy(() => import("./pages/company/master/MasterFilesOngoing"));
const MasterWorkflow = lazy(() => import("./pages/company/master/MasterWorkflow"));
const MasterTranslationStatus = lazy(() => import("./pages/company/master/MasterTranslationStatus"));
const MasterCurrencies = lazy(() => import("./pages/company/master/MasterCurrencies"));
const MasterLanguages = lazy(() => import("./pages/company/master/MasterLanguages"));
const MasterDeadlines = lazy(() => import("./pages/company/master/MasterDeadlines"));
const EmployeeFiles = lazy(() => import("./pages/employee/EmployeeFiles"));
const EmployeeDashboard = lazy(() => import("./pages/employee/EmployeeDashboard"));
const CompareEdit = lazy(() => import("./pages/employee/CompareEdit"));
const ProjectManagerProjects = lazy(() => import("./pages/employee/ProjectManagerProjects"));
const ResourceManagerOnGoing = lazy(() => import("./pages/employee/ResourceManagerOnGoing"));
const ResourceManagerFilesAll = lazy(() => import("./pages/employee/ResourceManagerFilesAll"));
const ResourceManagerFilesDone = lazy(() => import("./pages/employee/ResourceManagerFilesDone"));
const EmployeeGestaoDesempenho = lazy(() => import("./pages/employee/EmployeeGestaoDesempenho"));
const MasterProjects = lazy(() => import("./pages/company/master/MasterProjects"));
const ProjectDetails = lazy(() => import("./pages/company/master/ProjectDetails"));
const ProjectsBudget = lazy(() => import("./pages/company/master/ProjectsBudget"));
const ProjectsApproval = lazy(() => import("./pages/company/master/ProjectsApproval"));
const ProjectsDone = lazy(() => import("./pages/company/master/ProjectsDone"));
const ProjectsPaid = lazy(() => import("./pages/company/master/ProjectsPaid"));
const MasterCancelled = lazy(() => import("./pages/company/master/MasterCancelled"));
const MasterPendingRelease = lazy(() => import("./pages/company/master/MasterPendingRelease"));
const MasterPayments = lazy(() => import("./pages/company/master/MasterPayments"));
const MasterOnGoing = lazy(() => import("./pages/company/master/MasterOnGoing"));
const ProjectsApproved = lazy(() => import("./pages/company/master/ProjectsApproved"));
const ProjectsInAnalysis = lazy(() => import("./pages/company/master/ProjectsInAnalysis"));
const MasterDashboard = lazy(() => import("./pages/company/master/MasterDashboard"));
const ActivityLog = lazy(() => import("./pages/company/master/ActivityLog"));
const MasterAddProject = lazy(() => import("./pages/company/master/MasterAddProject"));
const MasterRefund = lazy(() => import("./pages/company/master/MasterRefund"));
const MasterDrafts = lazy(() => import("./pages/company/master/MasterDrafts"));
const MasterProfile = lazy(() => import("./pages/company/master/MasterProfile"));
const MasterAffiliates = lazy(() => import("./pages/company/master/MasterAffiliates"));
const MasterAffiliateDetails = lazy(() => import("./pages/company/master/MasterAffiliateDetails"));
const ManageFiles = lazy(() => import("./pages/company/master/ManageFiles"));
const MasterCredits = lazy(() => import("./pages/company/master/MasterCredits"));
const MasterAndamento = lazy(() => import("./pages/company/master/MasterAndamento"));
const MasterAndamentoArquivos = lazy(() => import("./pages/company/master/MasterAndamentoArquivos"));
const JobQueueManager = lazy(() => import("./pages/company/master/JobQueueManager"));
const MasterTaskManagement = lazy(() => import("./pages/company/master/MasterTaskManagement"));
const MasterEmployeePerformance = lazy(() => import("./pages/company/master/MasterEmployeePerformance"));
const MasterEmployeeDashboard = lazy(() => import("./pages/company/master/MasterEmployeeDashboard"));

// Affiliates (lazy — sob demanda)
const AffiliatesRegister = lazy(() => import("./pages/affiliates/AffiliatesRegister"));
const AffiliatesLogin = lazy(() => import("./pages/affiliates/AffiliatesLogin"));
const AffiliatesManage = lazy(() => import("./pages/affiliates/AffiliatesManage"));
const AffiliatesPayments = lazy(() => import("./pages/affiliates/AffiliatesPayments"));


const AppContent = () => {
  const [, setUnreadCount] = useState(0);
  const { user, loading } = useAuth();
  const location = useLocation();
  const isAffiliatesPublicPage =
    location.pathname === "/affiliates/login" ||
    location.pathname === "/affiliates/register";

  const shouldShowHeader =
    !location.pathname.startsWith("/affiliates") || isAffiliatesPublicPage;

  const isResetPasswordPage = location.pathname === "/reset-password";

  // O editor bicolunado roda em tela cheia — sem Header/sidebar globais.
  const isCompareEditPage = location.pathname.startsWith("/employee/compare/");

  // Landing da campanha Google Ads — cabeçalho próprio, sem o Header global.
  const isUscisCampaignPage = location.pathname.replace(/\/+$/, "") === "/traducao-certificada-uscis";

  useEffect(() => {
    if (loading || !user) {
      return;
    }

    const firestore = getFirestore();
    const uploadsCollection = collection(firestore, "uploads");

    const unsubscribe = safeOnSnapshot(
      uploadsCollection,
      (snapshot) => {
        const unreadUploads = snapshot.docs.filter(
          (doc) => !doc.data().isRead
        ).length;
        setUnreadCount(unreadUploads);
      },
      { label: "uploads" }
    );

    return () => unsubscribe();
  }, [user, loading]);

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 overflow-x-hidden" style={{ minHeight: "100vh", width: "100%", maxWidth: "100vw" }}>
      {/* Sincroniza o idioma com o prefixo da URL nas páginas públicas (SEO). */}
      <LanguageSync />
      {/* Google Ads: só páginas públicas + confirmação de pagamento. */}
      <GoogleAdsTracker />
      {/* Só mostra o Header global se NÃO for página de reset-password */}
      {shouldShowHeader && !isResetPasswordPage && !isCompareEditPage && !isUscisCampaignPage && <Header />}
      <main
        className={`overflow-x-hidden ${shouldShowHeader && !isResetPasswordPage && !isCompareEditPage && !isUscisCampaignPage ? "mt-[64px]" : ""}`}
        style={{ width: "100%", maxWidth: "100%" }}
      >
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
            </div>
          }
        >
        <Routes>
          {/* Rotas Públicas (PT = raiz) */}
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />

          {/* Versões por idioma das páginas públicas (SEO multilíngue).
              O idioma é fixado pela URL via <LanguageSync />. */}
          <Route path="/es" element={<Home />} />
          <Route path="/es/register" element={<Register />} />
          <Route path="/es/login" element={<Login />} />
          <Route path="/es/terms-of-use" element={<TermsOfUse />} />
          <Route path="/en" element={<Home />} />
          <Route path="/en/register" element={<Register />} />
          <Route path="/en/login" element={<Login />} />
          <Route path="/en/terms-of-use" element={<TermsOfUse />} />

          {/* Landing pages de SEO (Fase C) — PT (raiz) + /es + /en. */}
          {/* USCIS em PT = landing da campanha Google Ads; /es e /en seguem no LandingPage. */}
          <Route path="/traducao-certificada-uscis" element={<UscisCampaignLanding />} />
          {LANDING_PAGES.flatMap((p) => [
            p.key !== "certified-uscis" && <Route key={p.key} path={p.path} element={<LandingPage pageKey={p.key} basePath={p.path} />} />,
            <Route key={`${p.key}-es`} path={`/es${p.path}`} element={<LandingPage pageKey={p.key} basePath={p.path} />} />,
            <Route key={`${p.key}-en`} path={`/en${p.path}`} element={<LandingPage pageKey={p.key} basePath={p.path} />} />,
          ])}
          <Route path="/affiliates/register" element={<AffiliatesRegister />} />
          <Route path="/affiliates/login" element={<AffiliatesLogin />} />
          <Route
            path="/affiliates/manage"
            element={
              <ProtectedRoute allowedUserTypes={["affiliates"]}>
                <AffiliatesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/affiliates/payments"
            element={
              <ProtectedRoute allowedUserTypes={["affiliates"]}>
                <AffiliatesPayments />
              </ProtectedRoute>
            }
          />

          {/* Rotas do Cliente - Todas Protegidas */}
          <Route
            path="/client"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientProjects />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientProjects />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/dashboard"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientDashboard />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/files"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeFiles />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Página dinâmica de Dashboard (Financeiro / Desempenho) liberada
              pelo Master por cargo via employee_types.tabs → aba "dashboard"
              (as "colunas" da aba = os dashboards permitidos). */}
          <Route
            path="/employee/dashboard"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="dashboard">
                    <EmployeeDashboard />
                  </EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Dashboard de desempenho do próprio colaborador (liberado pelo
              Master por cargo via employee_types.tabs → aba "performance"). */}
          <Route
            path="/employee/performance"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="performance">
                    <MasterEmployeePerformance />
                  </EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Tela bicolunada de comparação/edição — full-bleed, sem layout
              padrão, pois o editor visual de PDF precisa da tela inteira. */}
          <Route
            path="/employee/compare/:workflowId"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <EmployeeTabRoute tabKey="files"><CompareEdit /></EmployeeTabRoute>
              </ProtectedRoute>
            }
          />
          {/* Aba de Projetos do Gerente de Projeto — espelha a do Master,
              acessível apenas a colaboradores (guard de cargo dentro da página). */}
          <Route
            path="/employee/projects"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <ProjectManagerProjects />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Aba "Em Andamento" (somente leitura) do Resource Manager. */}
          <Route
            path="/employee/ongoing"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <ResourceManagerOnGoing />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Aba "Todos Arquivos" do Resource Manager (espelho do Master, sem
              a coluna Cliente). */}
          <Route
            path="/employee/all-files"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <ResourceManagerFilesAll />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Aba "Arquivos Concluídos" do Resource Manager. */}
          <Route
            path="/employee/done-files"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <ResourceManagerFilesDone />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />

          {/* Abas espelho dos sublinks de "Projetos" do Master. Reusam os
              MESMOS componentes do Master (mesmos dados/ações); o acesso por
              cargo é controlado por EmployeeTabRoute via employee_types.tabs. */}
          <Route
            path="/employee/drafts"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="drafts"><MasterDrafts /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/projects-budget"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="projects-budget"><ProjectsBudget /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/projects-approval"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="projects-approval"><ProjectsApproval /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/projects-in-analysis"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="projects-in-analysis"><ProjectsInAnalysis /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/projects-done"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="projects-done"><ProjectsDone /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/projects-paid"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="projects-paid"><ProjectsPaid /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/projects-cancelled"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="projects-cancelled"><MasterCancelled /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/pending-release"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="pending-release"><MasterPendingRelease /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/payments"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="payments"><MasterPayments /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/refunds"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="refunds"><MasterRefund /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/add-project"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="add-project"><MasterAddProject /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/workflow"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="workflow"><MasterWorkflow /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          {/* Abas espelho dos sublinks de "Arquivos" do Master (andamento e
              desempenho). Reusam os mesmos componentes do Master. */}
          <Route
            path="/employee/andamento"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="andamento"><MasterAndamento /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/andamento-arquivos"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="andamento-arquivos"><MasterAndamentoArquivos /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/productivity"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="productivity"><MasterProductivity /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/files-ongoing"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="files-ongoing"><MasterFilesOngoing /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/job-queue"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="job-queue"><JobQueueManager /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/task-management"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="task-management"><MasterTaskManagement /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee/employee-performance"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <EmployeeTabRoute tabKey="employee-performance"><EmployeeGestaoDesempenho /></EmployeeTabRoute>
                </UniversalLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/employee/project/:id"
            element={
              <ProtectedRoute allowedUserTypes={["employee"]}>
                <UniversalLayout>
                  <ProjectDetails />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/profile"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientProfile />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-drafts"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientDrafts />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-budget"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientBudget />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-budget-received"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientBudgetReady />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-analysis"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientAnalysis />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-done"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientProjectsDone />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-paid"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientProjectsPaid />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/payments"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientPayments />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/going-on"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientGoingOn />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-refund"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientRefund />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects-divergence"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientDivergence />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects/clientaddproject"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientAddProject />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/projects/:projectId"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClienteProjectDetails />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/checkout"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <CheckoutPage />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/payment-success"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <PaymentSuccess />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/payment-error"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <PaymentError />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/add-collaborator"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c"]}>
                <UniversalLayout>
                  <AddCollaboratorColab />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/integrations"
            element={
              <ProtectedRoute allowedUserTypes={["b2b", "b2c", "colab"]}>
                <UniversalLayout>
                  <ClientIntegrations />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />

          {/* Rotas do Master - Todas Protegidas */}
          <Route
            path="/company/master"
            element={
              <ProtectedRoute allowedUserTypes={["master"]}>
                <UniversalLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="dashboard"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="andamento"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterAndamento />
                </ProtectedRoute>
              }
            />
            <Route
              path="andamento-arquivos"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterAndamentoArquivos />
                </ProtectedRoute>
              }
            />
            <Route
              path="job-queue"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <JobQueueManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="task-management"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterTaskManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="employee-performance"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeeDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="andamento/funcionario/:uid"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeePerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="activity-logs"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ActivityLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="affiliates"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterAffiliates />
                </ProtectedRoute>
              }
            />
            <Route
              path="affiliates/:affiliateId"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterAffiliateDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="manage-files"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ManageFiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="credits"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterCredits />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterClient />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/register"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeeRegister />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/types"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeeTypeRegister />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/manage"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeeManage />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/manage/:id"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeeManage />
                </ProtectedRoute>
              }
            />
            <Route
              path="email-templates"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmailTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="files/all"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterFilesAll />
                </ProtectedRoute>
              }
            />
            <Route
              path="files/ongoing"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterFilesOngoing />
                </ProtectedRoute>
              }
            />
            <Route
              path="files/done"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ResourceManagerFilesDone isMasterView />
                </ProtectedRoute>
              }
            />
            <Route
              path="files/productivity"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterProductivity />
                </ProtectedRoute>
              }
            />
            <Route
              path="files/observations"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterFileObservations />
                </ProtectedRoute>
              }
            />
            <Route
              path="workflow"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterWorkflow />
                </ProtectedRoute>
              }
            />
            <Route
              path="translation-statuses"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterTranslationStatus />
                </ProtectedRoute>
              }
            />
            <Route
              path="currencies"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterCurrencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="languages"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterLanguages />
                </ProtectedRoute>
              }
            />
            <Route
              path="deadlines"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterDeadlines />
                </ProtectedRoute>
              }
            />
            <Route
              path="employee-payments"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterEmployeePayments />
                </ProtectedRoute>
              }
            />
            <Route
              index
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterProjects />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-budget"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ProjectsBudget />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-approval"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ProjectsApproval />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-approved"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ProjectsApproved />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-in-analysis"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ProjectsInAnalysis />
                </ProtectedRoute>
              }
            />
            <Route
              path="ongoing"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterOnGoing />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-done"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ProjectsDone />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-paid"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <ProjectsPaid />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects-cancelled"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterCancelled />
                </ProtectedRoute>
              }
            />
            <Route
              path="pending-release"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterPendingRelease />
                </ProtectedRoute>
              }
            />
            <Route
              path="payments"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="refunds"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterRefund />
                </ProtectedRoute>
              }
            />

            <Route
              path="add-project"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterAddProject />
                </ProtectedRoute>
              }
            />
            <Route
              path="drafts"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterDrafts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/company/master/profile"
              element={
                <ProtectedRoute allowedUserTypes={["master"]}>
                  <MasterProfile />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/company/master/project/:id"
            element={
              <ProtectedRoute allowedUserTypes={["master"]}>
                <UniversalLayout>
                  <ProjectDetails />
                </UniversalLayout>
              </ProtectedRoute>
            }
          />

          {/* Rota de Pagamento - PÚBLICA */}
          <Route path="/payment" element={<ApplePayTest />} />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <UnreadCountProvider>
              <AppContent />
          </UnreadCountProvider>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
